import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Caption } from '@shared/models/step.model';
import { VgApiService } from '@videogular/ngx-videogular/core';
import { PlIcon } from '@app/icon-library/icons';
import { MenuItemTooltip } from '@app/components/menuTooltip/menuTooltip.component';
import { I18nService } from '@app/i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Breakpoint, WindowEventService } from '@shared/services/window.event.service';
import { firstValueFrom } from 'rxjs';
import { ButtonSwitcherOption } from '@app/components/video/buttonSwitcher/buttonSwitcher.component';

export enum VideoQuality {
  hd = 'HD',
  sd = 'SD',
}

let identifier: number = 0;

export const KEY_LANGUAGE_PREFERENCE: string = 'pl3-video-language';
export const KEY_QUALITY_PREFERENCE: string = 'pl3-video-quality';
export const KEY_SPEED_PREFERENCE: string = 'pl3-video-speed';
export const KEY_VOLUME_PREFERENCE: string = 'pl3-video-volume';

@Component({
  selector: 'pl-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VideoComponent implements OnInit, AfterViewInit {
  @Input() public poster: string | undefined;
  @Input() public sourceHD: string | undefined;
  @Input() public sourceSD: string | undefined;
  @Input() public captions: Caption[] = [];

  @ViewChild('vgMedia') public vgMedia!: ElementRef;
  @ViewChild('vgMediaSource') public vgMediaSource!: ElementRef;

  public textTrackMenuItems: MenuItemTooltip[] = [];
  public playbackRateOptions: ButtonSwitcherOption[] = this.getPlaybackRateOptions();
  public videoQualityOptions: ButtonSwitcherOption[] = [];

  public sourceSelectedByDefault: string | null = null;
  public textTrackSelectedByDefault: string | null = this.getSubtitleLangStored();

  public api!: VgApiService;

  public identifier: string = `pl-video-${identifier++}`;

  public readonly PlIcon: typeof PlIcon = PlIcon;

  private destroyRef: DestroyRef = inject(DestroyRef);

  public isPlayerReady: boolean = false;

  @HostListener('document:keyup.space', ['$event'])
  public handleSpaceKeyPressed(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (this.vgMedia && this.vgMedia.nativeElement) {
      this.playPause();
    }
  }

  constructor(
    private windowEventService: WindowEventService,
    private i18nService: I18nService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.videoQualityOptions = await this.getVideoQualityOptions();
    this.textTrackMenuItems = this.getTextTrackMenuItems();
  }

  public ngAfterViewInit(): void {
    const playbackRateStored: number | null = this.getPlaybackRateStored();
    if (playbackRateStored) {
      this.vgVideo.playbackRate = playbackRateStored;
    }

    const volumeStored: number | null = this.getVolumeStored();
    if (volumeStored) {
      this.vgVideo.volume = volumeStored;
    }
  }

  private async getVideoQualityOptions(): Promise<ButtonSwitcherOption[]> {
    const videoQualityOptions: ButtonSwitcherOption[] = [];
    const defaultQuality: VideoQuality = await this.computeQualityToUseByDefault();
    if (this.sourceHD) {
      videoQualityOptions.push({
        label: VideoQuality.hd,
        value: this.sourceHD,
        displayByDefault: defaultQuality === VideoQuality.hd,
        click: () => this.changeVideoQuality(VideoQuality.hd, this.sourceHD),
      });
    }
    if (this.sourceSD) {
      videoQualityOptions.push({
        label: VideoQuality.sd,
        value: this.sourceSD,
        displayByDefault: defaultQuality === VideoQuality.sd,
        click: () => this.changeVideoQuality(VideoQuality.sd, this.sourceSD),
      });
    }

    if (videoQualityOptions.length === 0) {
      throw new Error(`We should have at least one video track (SD or HD), none found`);
    } else if (videoQualityOptions.length === 1) {
      this.sourceSelectedByDefault = `${videoQualityOptions[0].value}`;
    } else {
      const defaultOptionValue: string | number | undefined = videoQualityOptions.find(
        (option: ButtonSwitcherOption) => option.displayByDefault,
      )?.value;
      this.sourceSelectedByDefault = defaultOptionValue ? `${defaultOptionValue}` : null;
    }

    return videoQualityOptions;
  }

  private getSubtitleLangStored(): string | null {
    return localStorage.getItem(KEY_LANGUAGE_PREFERENCE);
  }

  private setSubtitleLangInLocalStorage(lang: string | null): void {
    if (lang) {
      localStorage.setItem(KEY_LANGUAGE_PREFERENCE, lang);
    } else {
      localStorage.removeItem(KEY_LANGUAGE_PREFERENCE);
    }
  }

  private async computeQualityToUseByDefault(): Promise<VideoQuality> {
    const qualitySettingStored: VideoQuality | null = this.getQualityStored();
    if (qualitySettingStored) {
      return qualitySettingStored;
    } else {
      const screenSize: number = await firstValueFrom(this.windowEventService.getWindowResizeEvent());
      return screenSize < Breakpoint.lg ? VideoQuality.sd : VideoQuality.hd;
    }
  }

  private getQualityStored(): VideoQuality | null {
    const qualityStored: string | null = localStorage.getItem(KEY_QUALITY_PREFERENCE);
    return qualityStored ? (qualityStored as VideoQuality) : null;
  }

  private setQualityInLocalStorage(quality: VideoQuality): void {
    localStorage.setItem(KEY_QUALITY_PREFERENCE, quality);
  }

  private getPlaybackRateStored(): number | null {
    const valueStored: string | null = localStorage.getItem(KEY_SPEED_PREFERENCE);
    return valueStored ? +valueStored : null;
  }

  private setPlaybackRateInLocalStorage(playbackRate: number): void {
    localStorage.setItem(KEY_SPEED_PREFERENCE, `${playbackRate}`);
  }

  private getVolumeStored(): number | null {
    const valueStored: string | null = localStorage.getItem(KEY_VOLUME_PREFERENCE);
    return valueStored ? +valueStored : null;
  }

  private setVolumeInLocalStorage(volume: number): void {
    localStorage.setItem(KEY_VOLUME_PREFERENCE, `${volume}`);
  }

  private getTextTrackMenuItems(): MenuItemTooltip[] {
    const allTracksMenuItems: MenuItemTooltip[] = [
      {
        label: this.i18nService.translate('blocks.video.subtitles_off'),
        value: null,
        displayByDefault: this.textTrackSelectedByDefault === null,
        click: () => this.changeTextTrack(null),
      },
    ];
    allTracksMenuItems.push(
      ...this.captions.map((caption: Caption) => {
        return {
          label: caption.label,
          value: caption.src,
          displayByDefault: this.textTrackSelectedByDefault === caption.lang,
          click: () => this.changeTextTrack(caption.lang),
        };
      }),
    );

    return allTracksMenuItems;
  }

  public onPlayerReady(api: VgApiService): void {
    this.api = api;
    setTimeout((): void => {
      this.isPlayerReady = true;
    });

    this.videoApi.subscriptions.rateChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const playbackRate: number = this.vgVideo.playbackRate;
      this.setPlaybackRateInLocalStorage(playbackRate);
    });

    this.videoApi.subscriptions.volumeChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const volume: number = this.vgVideo.volume;
      this.setVolumeInLocalStorage(volume);
    });
  }

  public get videoApi(): VgApiService {
    if (this.api) {
      return this.api;
    }

    throw new Error(`Can't find VgApiService, something is wrong`);
  }

  public get vgVideo(): HTMLVideoElement {
    if (this.vgMedia && this.vgMedia.nativeElement) {
      return this.vgMedia.nativeElement;
    }

    throw new Error(`vgMedia not initialised (or called too early)`);
  }

  public get vgSource(): HTMLVideoElement {
    if (this.vgMediaSource && this.vgMediaSource.nativeElement) {
      return this.vgMediaSource.nativeElement;
    }

    throw new Error(`vgMediaSource not initialised (or called too early)`);
  }

  public changeVideoQuality(videoQualityToChange: VideoQuality, src: string | undefined): Promise<void> {
    this.setQualityInLocalStorage(videoQualityToChange);
    if (src) {
      this.vgVideo.pause();
      const currentTime: number = this.vgVideo.currentTime;
      this.vgSource.setAttribute('src', src);
      this.vgVideo.load();
      this.vgVideo.currentTime = currentTime;
      return this.vgVideo.play();
    } else {
      throw new Error(`Source is undefined when changing, this should not happen`);
    }
  }

  private playPause(): void {
    // Conditions to know that a video is playing: https://stackoverflow.com/a/6877530/4834117
    if (this.vgVideo.currentTime > 0 && !this.vgVideo.paused && !this.vgVideo.ended && this.vgVideo.readyState > 2) {
      this.vgVideo.pause();
    } else {
      this.vgVideo.play();
    }
  }

  public changeTextTrack(lang: string | null): void {
    this.setSubtitleLangInLocalStorage(lang);
    const allTextTrack: TextTrackList = this.vgVideo.textTracks;
    for (let index: number = 0; index < allTextTrack.length; index++) {
      const singleTextTrack: TextTrack = allTextTrack[index];
      singleTextTrack.mode = singleTextTrack.language === lang ? 'showing' : 'disabled';
    }
  }

  private onPlaybackRateChanged(playbackRate: number): void {
    this.vgVideo.playbackRate = playbackRate;
  }

  private getPlaybackRateOptions(): ButtonSwitcherOption[] {
    const rates: number[] = [0.75, 1, 1.25, 1.5];
    const playbackRateStored: number = this.getPlaybackRateStored() || 1;
    return rates.map((rate: number) => {
      return {
        label: `${rate}x`,
        value: rate,
        displayByDefault: playbackRateStored === rate,
        click: () => this.onPlaybackRateChanged(rate),
      };
    });
  }
}
